import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminConsentDocumentsApi,
  normalizeConsentDocumentMaster,
  organizationConsentModeApi,
  publicConsentApi,
  RequestError,
  type ConsentDocumentStatus,
  type ConsentDocumentSummary,
  type PublicConsentDocument,
} from '../api/client';
import { findCategoryByValues } from '../utils/consentDocumentCategories';

/** API が返す raw 値（analysis / voice 等）を画面表示用の日本語ラベルに変換する。未登録は元の値を返す */
function translateValue(t: TFunction, namespace: string, value: string): string {
  const key = `admin.consentDocs.${namespace}.${value}`;
  const translated = t(key, { defaultValue: '' });
  return translated || value;
}

const STATUS_FILTERS: ConsentDocumentStatus[] = ['draft', 'published'];

function formatDateTimeJst(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function sortDocs(list: ConsentDocumentSummary[]): ConsentDocumentSummary[] {
  return [...list].sort((a, b) => {
    if (a.purpose !== b.purpose) return a.purpose.localeCompare(b.purpose, 'ja-JP');
    if (a.data_type !== b.data_type) return a.data_type.localeCompare(b.data_type, 'ja-JP');
    return b.version.localeCompare(a.version, 'ja-JP', { numeric: true });
  });
}

/** ADM-S06 同意文書管理（一覧） */
export default function AdminConsentDocuments() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<ConsentDocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilters, setStatusFilters] =
    useState<ConsentDocumentStatus[]>(STATUS_FILTERS);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [purposeFilter, setPurposeFilter] = useState<string>('');
  const [dataTypeFilter, setDataTypeFilter] = useState<string>('');
  const [purposeOptions, setPurposeOptions] = useState<string[]>([]);
  const [dataTypeOptions, setDataTypeOptions] = useState<string[]>([]);
  // 招待受諾画面で実際に表示されている文書（BE の公開エンドポイントから取得）
  const [inviteBundleDocs, setInviteBundleDocs] = useState<PublicConsentDocument[]>([]);
  const [inviteBundleError, setInviteBundleError] = useState<string | null>(null);
  const [inviteBundleTenantCode, setInviteBundleTenantCode] = useState<string | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    adminConsentDocumentsApi
      .getMaster(token)
      .then((res) => {
        const master = normalizeConsentDocumentMaster(res);
        setPurposeOptions(master.purposes);
        setDataTypeOptions(master.data_types);
      })
      .catch(() => {
        /* マスタ取得に失敗した場合はフィルタを空のまま残す（フィルタ非表示） */
      });
  }, [getToken]);

  // 招待受諾画面の実際の表示内容を取得: 自テナントの tenant_code を取ってから
  // 未認証 GET /v1/public/consent-documents を叩く（admin も同じ公開 API を見る）
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    setInviteBundleError(null);
    organizationConsentModeApi
      .getSelf(token)
      .then((tenant) => {
        if (cancelled) return;
        setInviteBundleTenantCode(tenant.tenant_code);
        return publicConsentApi.getByTenant(tenant.tenant_code);
      })
      .then((res) => {
        if (cancelled || !res) return;
        setInviteBundleDocs(res.documents ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // BE 未実装時（404）はバナーを静かに非表示
        if (err instanceof RequestError && err.status === 404) {
          setInviteBundleDocs([]);
          return;
        }
        setInviteBundleError(err instanceof Error ? err.message : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    const statusParam: ConsentDocumentStatus[] = includeArchived
      ? Array.from(new Set<ConsentDocumentStatus>([...statusFilters, 'archived']))
      : statusFilters;
    adminConsentDocumentsApi
      .list(token, {
        status: statusParam,
        include_archived: includeArchived,
        purpose: purposeFilter || undefined,
        data_type: dataTypeFilter || undefined,
      })
      .then((res) => setList(sortDocs(res.data ?? [])))
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, statusFilters, includeArchived, purposeFilter, dataTypeFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = (status: ConsentDocumentStatus) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const groupedCounts = useMemo(() => {
    const counts: Record<ConsentDocumentStatus, number> = { draft: 0, published: 0, archived: 0 };
    list.forEach((doc) => {
      counts[doc.status] = (counts[doc.status] ?? 0) + 1;
    });
    return counts;
  }, [list]);

  return (
    <div className="page admin-consent-docs-page">
      <h1>{t('admin.consentDocs.title')}</h1>
      <p className="admin-consent-docs-intro">{t('admin.consentDocs.intro')}</p>

      <div className="admin-card admin-consent-docs-actions">
        <Link to="/admin/consent/new" className="admin-btn-primary">
          {t('admin.consentDocs.newDocument')}
        </Link>
      </div>

      <div className="admin-card admin-consent-docs-invite-bundle">
        <h3>{t('admin.consentDocs.inviteBundle.title')}</h3>
        <p className="admin-form-hint">{t('admin.consentDocs.inviteBundle.intro')}</p>
        {inviteBundleError && (
          <p className="admin-form-error">{inviteBundleError}</p>
        )}
        {!inviteBundleError && inviteBundleDocs.length === 0 && (
          <p className="admin-empty">{t('admin.consentDocs.inviteBundle.empty')}</p>
        )}
        {inviteBundleDocs.length > 0 && (
          <ul className="admin-consent-docs-invite-bundle-list">
            {inviteBundleDocs.map((doc) => (
              <li key={doc.id}>
                <span className="admin-consent-docs-invite-bundle-title">{doc.title}</span>
                <span className="admin-consent-docs-invite-bundle-meta">
                  {t('admin.consentDocs.inviteBundle.metaLive', {
                    version: doc.version,
                    mode:
                      doc.consent_mode_label_ja ||
                      t(`admin.consentDocs.consentModeValue.${doc.consent_mode}`, { defaultValue: doc.consent_mode }),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {inviteBundleTenantCode && (
          <p className="admin-form-hint">
            {t('admin.consentDocs.inviteBundle.tenantLine', { code: inviteBundleTenantCode })}
          </p>
        )}
        <p className="admin-consent-docs-invite-bundle-note" role="note">
          {t('admin.consentDocs.inviteBundle.noteLive')}
        </p>
      </div>

      <div className="admin-card admin-consent-docs-filters">
        <h3>{t('admin.consentDocs.filterTitle')}</h3>
        <div className="admin-consent-docs-filter-row">
          {STATUS_FILTERS.map((status) => (
            <label key={status} className="admin-consent-docs-filter-checkbox">
              <input
                type="checkbox"
                checked={statusFilters.includes(status)}
                onChange={() => toggleStatus(status)}
              />
              {t(`admin.consentDocs.status.${status}`)}
              {groupedCounts[status] > 0 && (
                <span className="admin-consent-docs-count">{groupedCounts[status]}</span>
              )}
            </label>
          ))}
          <label className="admin-consent-docs-filter-checkbox">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t('admin.consentDocs.filterIncludeArchived')}
            {groupedCounts.archived > 0 && (
              <span className="admin-consent-docs-count">{groupedCounts.archived}</span>
            )}
          </label>
        </div>
        {(purposeOptions.length > 0 || dataTypeOptions.length > 0) && (
          <div className="admin-consent-docs-filter-row">
            {purposeOptions.length > 0 && (
              <label className="admin-consent-docs-filter-select">
                <span>{t('admin.consentDocs.col.purpose')}</span>
                <select
                  value={purposeFilter}
                  onChange={(e) => setPurposeFilter(e.target.value)}
                >
                  <option value="">{t('admin.consentDocs.filterAll')}</option>
                  {purposeOptions.map((p) => (
                    <option key={p} value={p}>{translateValue(t, 'purposeValue', p)}</option>
                  ))}
                </select>
              </label>
            )}
            {dataTypeOptions.length > 0 && (
              <label className="admin-consent-docs-filter-select">
                <span>{t('admin.consentDocs.col.dataType')}</span>
                <select
                  value={dataTypeFilter}
                  onChange={(e) => setDataTypeFilter(e.target.value)}
                >
                  <option value="">{t('admin.consentDocs.filterAll')}</option>
                  {dataTypeOptions.map((d) => (
                    <option key={d} value={d}>{translateValue(t, 'dataTypeValue', d)}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="admin-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="admin-card">
          {list.length === 0 ? (
            <p className="admin-empty">{t('admin.consentDocs.empty')}</p>
          ) : (
            <table className="admin-table admin-consent-docs-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.consentDocs.col.category')}</th>
                  <th scope="col">{t('admin.consentDocs.col.version')}</th>
                  <th scope="col">{t('admin.consentDocs.col.title')}</th>
                  <th scope="col">{t('admin.consentDocs.col.status')}</th>
                  <th scope="col">{t('admin.consentDocs.col.showOnInvite')}</th>
                  <th scope="col">{t('admin.consentDocs.col.effectiveFrom')}</th>
                  <th scope="col">{t('admin.consentDocs.col.updatedAt')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((doc) => {
                  const category = findCategoryByValues({
                    purpose: doc.purpose,
                    data_type: doc.data_type,
                    consent_mode: doc.consent_mode,
                    version: doc.version,
                  });
                  // BE が返す *_label_ja を優先し、未同梱の場合は translateValue でフォールバック
                  const docLabel = doc as ConsentDocumentSummary & {
                    purpose_label_ja?: string;
                    data_type_label_ja?: string;
                  };
                  const purposeLabel =
                    docLabel.purpose_label_ja || translateValue(t, 'purposeValue', doc.purpose);
                  const dataTypeLabel =
                    docLabel.data_type_label_ja || translateValue(t, 'dataTypeValue', doc.data_type);
                  return (
                  <tr key={doc.id}>
                    <td>
                      {category ? (
                        category.label
                      ) : (
                        <span className="admin-consent-docs-category-other">
                          {t('admin.consentDocs.category.other')}（{purposeLabel} / {dataTypeLabel}）
                        </span>
                      )}
                    </td>
                    <td><code>{doc.version}</code></td>
                    <td>{doc.title}</td>
                    <td>
                      <span className={`admin-badge admin-consent-docs-status-${doc.status}`}>
                        {t(`admin.consentDocs.status.${doc.status}`)}
                      </span>
                    </td>
                    <td>
                      {doc.show_on_invite_page ? (
                        <span className="admin-badge admin-badge-ok">
                          {t('admin.consentDocs.inviteBundle.flagOn')}
                        </span>
                      ) : (
                        <span className="admin-badge">{t('admin.consentDocs.inviteBundle.flagOff')}</span>
                      )}
                    </td>
                    <td>{formatDateTimeJst(doc.effective_from)}</td>
                    <td>{formatDateTimeJst(doc.updated_at)}</td>
                    <td>
                      <Link to={`/admin/consent/${doc.id}`}>
                        {doc.status === 'draft'
                          ? t('admin.consentDocs.action.edit')
                          : t('admin.consentDocs.action.view')}
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="admin-card admin-consent-docs-guidance">
        <h3>{t('admin.consentDocs.guidanceTitle')}</h3>
        <p>{t('admin.consentDocs.guidanceIntro')}</p>
        <ul>
          <li>{t('admin.consentDocs.guidanceReconsent')}</li>
          <li>{t('admin.consentDocs.guidanceAudit')}</li>
          <li>{t('admin.consentDocs.guidanceVersion')}</li>
          <li>{t('admin.consentDocs.guidanceArchive')}</li>
        </ul>
        <p className="admin-consent-docs-note" role="note">
          {t('admin.consentDocs.guidanceNote')}
        </p>
      </div>

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
